import React from "react";
import styled from "styled-components";
import {
    Box,
    MenuItem,
    Select,
    SelectChangeEvent,
    ThemeProvider,
    Theme,
    StyledEngineProvider,
    Tooltip,
} from "@mui/material";
import { AutoBind } from "../../rule-engine-core/Common/AutoBind";
import { IContext } from "../../rule-engine-core/Common/IContext";
import { theme } from "../UITheme";
import { DeleteIcon } from "../Icon/DeleteIcon";
import { CutIcon } from "../Icon/CutIcon";
import { CopyIcon } from "../Icon/CopyIcon";
import { CloseIcon } from "../Icon/CloseIcon";
import { PasteIcon } from "../Icon/PasteIcon";
import { ObserveState } from "../StateManagement/ObserveState";
import { InlineTextEdit } from "../InlineEditor/InlineTextEdit";
import { UIButton } from "../BuildlingBlocks/UIButton";
import { UILabel } from "../BuildlingBlocks/UILabel";
import { UIStyleConstants } from "../../rule-engine-core/Common/UIStyleConstants";
import { TopbarIconButton } from "../CommonStyles/TopbarIconButton";
import { TopbarIconBelowLabelDiv } from "../CommonStyles/TopbarIconBelowLabelDiv";
import { IconButtonWithTitle } from "../CommonStyles/IconButtonWithTitle";
import { TopBarStyledButtonGroup } from "../CommonStyles/TopBarStyledButtonGroup";

declare module "@mui/styles/defaultTheme" {
    // eslint-disable-next-line @typescript-eslint/no-empty-interface
    interface DefaultTheme extends Theme {}
}

export interface IInjectElem {
    wrapperStyle?: object;
    elem: JSX.Element;
}

export enum IInjectTopItemType {
    SELECT = "select",
    ICONBUTTONGROUP = "icon button group",
}

export interface IInjectTopItem {
    value?: string;
    label?: string;
    items?: {
        value?: string;
        label?: string;
        icon?: JSX.Element;
        belowLabel?: JSX.Element;
        isSelected?: () => boolean;
        onMouseDown?: () => void;
        onMouseUp?: () => void;
    }[];
    type: IInjectTopItemType;
    wrapperStyle?: object;
    onSelect: (value: string) => void;
}

class TopbarActionGroup extends React.Component<{ editContext: IContext; isActionDisabled?: boolean }> {
    render() {
        return (
            <ObserveState
                listenTo={this.props.editContext}
                control={() => (
                    <TopBarStyledButtonGroup
                        variant="outlined"
                        size="small"
                    >
                        <IconButtonWithTitle
                            title="Copy"
                            icon={<CopyIcon />}
                            disabled={this.props.isActionDisabled}
                            testId="btn-topbar-copy"
                            action={this.props.editContext.onCopy}
                        />
                        <IconButtonWithTitle
                            title="Cut"
                            icon={<CutIcon />}
                            disabled={this.props.isActionDisabled}
                            testId="btn-topbar-cut"
                            action={this.props.editContext.onCut}
                        />
                        <IconButtonWithTitle
                            title="Paste"
                            icon={<PasteIcon />}
                            disabled={this.props.isActionDisabled || !this.props.editContext.clipboardElement}
                            testId="btn-topbar-paste"
                            action={this.props.editContext.onPaste}
                        />
                        <IconButtonWithTitle
                            title="Delete"
                            icon={<DeleteIcon />}
                            disabled={this.props.isActionDisabled}
                            testId="btn-topbar-delete"
                            action={this.props.editContext.onDelete}
                        />
                    </TopBarStyledButtonGroup>
                )}
            />
        );
    }
}

export class Topbar extends React.Component<{
    editContext: IContext;
    onSave?: (data?: object) => void;
    onClose?: () => void;
    isRequiredActionGroup: boolean;
    isActionDisabled?: boolean;
    prevElems?: (IInjectElem | JSX.Element)[];
    prevItems?: IInjectTopItem[];
    afterItems?: IInjectTopItem[];
    afterElems?: (IInjectElem | JSX.Element)[];
    name?: string;
    onChangeName: (newName: string) => void;
}> {
    @AutoBind
    renderTopItem(item: IInjectTopItem): JSX.Element | null {
        if (item.type === IInjectTopItemType.SELECT) {
            return (
                <TopbarItemDiv>
                    <StyledEngineProvider injectFirst>
                        <ThemeProvider theme={theme}>
                            {item.label && (
                                <UILabel
                                    paddingRight={"10px"}
                                    variant={"small"}
                                >
                                    {item.label}
                                </UILabel>
                            )}
                            {item.items && item.items.length > 0 && (
                                <Select
                                    value={item.value as string}
                                    onChange={(event: SelectChangeEvent) => {
                                        item.onSelect(event.target.value + "");
                                    }}
                                    variant={"outlined"}
                                    size={"small"}
                                    style={{
                                        width: "150px",
                                        height: "32px",
                                        fontSize: 12,
                                        fontWeight: 500,
                                        borderColor: UIStyleConstants.GRAY_DARK_COLOR,
                                        color: UIStyleConstants.GRAY_DARK_COLOR,
                                        fontFamily: UIStyleConstants.FONT_FAMILY,
                                    }}
                                >
                                    {item.items &&
                                        item.items.map((m, idx) => (
                                            <MenuItem
                                                style={{
                                                    fontSize: 12,
                                                    fontWeight: 500,
                                                    color: UIStyleConstants.GRAY_DARK_COLOR,
                                                    fontFamily: UIStyleConstants.FONT_FAMILY,
                                                }}
                                                key={idx}
                                                value={m.value || m.label}
                                            >
                                                {m.label || m.value}
                                            </MenuItem>
                                        ))}
                                </Select>
                            )}
                        </ThemeProvider>
                    </StyledEngineProvider>
                </TopbarItemDiv>
            );
        } else if (item.type === IInjectTopItemType.ICONBUTTONGROUP) {
            return (
                <TopbarItemDiv>
                    <StyledEngineProvider injectFirst>
                        <ThemeProvider theme={theme}>
                            {item.label && (
                                <UILabel
                                    paddingRight={"10px"}
                                    variant={"small"}
                                >
                                    {item.label}
                                </UILabel>
                            )}
                            <TopBarStyledButtonGroup
                                variant="outlined"
                                size="small"
                                style={item.wrapperStyle}
                            >
                                {item.items &&
                                    item.items.length > 0 &&
                                    item.items.map((m, idx) => {
                                        let isSelected = false;
                                        if (m.isSelected) {
                                            isSelected = m.isSelected();
                                        } else if (item.value === (m.value || m.label)) {
                                            isSelected = true;
                                        }
                                        return (
                                            <Tooltip
                                                title={m.label || m.value || ""}
                                                key={idx}
                                            >
                                                <TopbarIconButton
                                                    data-testid={`btn-mode-${(m.label || m.value || "")
                                                        .replace(/\s/, "-")
                                                        .replace(/\(/, "")
                                                        .replace(/\)/, "")
                                                        .toLocaleLowerCase()}`}
                                                    classes={{
                                                        root: isSelected ? "TopbarIconButton-selected" : undefined,
                                                    }}
                                                    onClick={() => item.onSelect(m.value || m.label || "")}
                                                    onMouseDown={m.onMouseDown}
                                                    onMouseUp={m.onMouseUp}
                                                    onMouseLeave={m.onMouseUp}
                                                >
                                                    {m.icon}

                                                    {m.belowLabel && (
                                                        <TopbarIconBelowLabelDiv>
                                                            {m.belowLabel}
                                                        </TopbarIconBelowLabelDiv>
                                                    )}
                                                </TopbarIconButton>
                                            </Tooltip>
                                        );
                                    })}
                            </TopBarStyledButtonGroup>
                        </ThemeProvider>
                    </StyledEngineProvider>
                </TopbarItemDiv>
            );
        }

        return null;
    }

    render() {
        return (
            <Box
                display={"flex"}
                style={{
                    width: "100%",
                    overflow: "auto",
                    borderBottom: "1px solid " + UIStyleConstants.GRAY_LIGHTER_COLOR,
                }}
            >
                <TopbarDiv>
                    {this.props.name && (
                        <TopbarTitleDiv
                            data-testid="topbar-name"
                            content={this.props.name}
                        >
                            <InlineTextEdit
                                text={this.props.name}
                                onEdit={this.props.onChangeName}
                            />
                        </TopbarTitleDiv>
                    )}
                    {(this.props.prevElems || []).map((elem, idx) => (
                        <TopbarItemDiv
                            key={idx}
                            style={Reflect.get(elem, "wrapperStyle") || {}}
                        >
                            <StyledEngineProvider injectFirst>
                                <ThemeProvider theme={theme}>
                                    {React.isValidElement(elem) ? elem : Reflect.get(elem, "elem")}
                                </ThemeProvider>
                            </StyledEngineProvider>
                        </TopbarItemDiv>
                    ))}

                    {this.props.prevItems &&
                        this.props.prevItems.map((item, idx) => (
                            <React.Fragment key={idx}>{this.renderTopItem(item)}</React.Fragment>
                        ))}

                    {this.props.isRequiredActionGroup && (
                        <TopbarItemDiv
                            style={{
                                marginRight:
                                    (this.props.afterElems || []).length === 0 &&
                                    (this.props.afterItems || []).length === 0
                                        ? "auto"
                                        : "0",
                            }}
                        >
                            <StyledEngineProvider injectFirst>
                                <ThemeProvider theme={theme}>
                                    <TopbarActionGroup
                                        editContext={this.props.editContext}
                                        isActionDisabled={this.props.isActionDisabled}
                                    />
                                </ThemeProvider>
                            </StyledEngineProvider>
                        </TopbarItemDiv>
                    )}
                    {this.props.afterItems &&
                        this.props.afterItems.map((item, idx) => (
                            <React.Fragment key={idx}>{this.renderTopItem(item)}</React.Fragment>
                        ))}
                    {(this.props.afterElems || []).map((elem, idx) => (
                        <TopbarItemDiv
                            key={idx}
                            style={Reflect.get(elem, "wrapperStyle")}
                        >
                            <StyledEngineProvider injectFirst>
                                <ThemeProvider theme={theme}>
                                    {React.isValidElement(elem) ? elem : Reflect.get(elem, "elem")}
                                </ThemeProvider>
                            </StyledEngineProvider>
                        </TopbarItemDiv>
                    ))}
                    {this.props.onSave && (
                        <TopbarItemDiv style={{ marginLeft: "auto" }}>
                            <UIButton
                                onClick={this.props.onSave}
                                variant="contained"
                                data-testid="btn-topbar-save"
                            >
                                Save
                            </UIButton>
                        </TopbarItemDiv>
                    )}
                    {this.props.onClose && (
                        <TopbarItemDiv style={{ marginLeft: this.props.onSave ? undefined : "20px" }}>
                            <TopbarIconButton
                                onClick={this.props.onClose}
                                data-testid="btn-topbar-close"
                            >
                                <CloseIcon />
                            </TopbarIconButton>
                        </TopbarItemDiv>
                    )}
                    <Box paddingRight={"10px"} />
                </TopbarDiv>
            </Box>
        );
    }
}

const TopbarDiv = styled.div`
    display: flex;
    width: 100%;
    align-items: center;
    height: 65px;
`;

export const TopbarTitleDiv = styled.div<{ content: string }>`
    position: relative;
    display: flex;
    align-items: center;
    min-width: 200px;

    .InlineEdit {
        width: 100%;
        top: 0;
        left: 0;
        color: ${UIStyleConstants.TEXT_COLOR_DEFAULT};
        font-weight: bold;
        font-size: 24px;
    }
`;

export const TopbarItemDiv = styled.div`
    display: flex;
    align-items: center;
    flex-direction: row;
    justify-content: flex-start;
    margin-left: 18px;
`;
